import { useState } from 'react';
import styled from 'styled-components';
import { api } from '../api/client';
import { getIdentity } from '../lib/identity';
import { isOffensive } from '../lib/badwords';
import { useStore } from '../stores/store';

// Aviso dirigido a los jugadores que quedaron con el alias por defecto (nick = los primeros 8
// caracteres del pub, "letras al azar"): les pedimos disculpas y les ofrecemos elegir un apodo
// lindo para verse mejor en los rankings. Aparece SOLO si su nick sigue siendo ese código.
export function NickNudge() {
  const nick = useStore((s) => s.nick);
  const setNick = useStore((s) => s.setNick);
  const pub = (() => {
    try {
      return getIdentity().pub;
    } catch {
      return '';
    }
  })();

  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // El alias por defecto son los 8 primeros hex del pub. Si el nick coincide, es un jugador "sin nombre".
  const isDefaultAlias = !!nick && !!pub && nick === pub.slice(0, 8);
  if (!isDefaultAlias || dismissed) return null;

  const bad = value.trim().length >= 2 && isOffensive(value);
  const ok = value.trim().length >= 2 && !bad;

  const save = async () => {
    if (!ok || saving) return;
    setSaving(true);
    try {
      const r = await api.timbaSetNick(pub, value.trim());
      if (r?.nick) setNick(r.nick);
      setDismissed(true);
    } catch {
      // Si falla la red, dejamos el aviso para reintentar (no lo cerramos).
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card role="region" aria-label="Elegí tu apodo">
      <div className="hd">
        <span className="emoji">🙈</span>
        <div>
          <h3>¡Perdón! Te dejamos con un nombre feo</h3>
          <p>
            Apareces en los rankings como <b>«{nick}»</b> —un código, no un apodo. Fue culpa nuestra.
            Elegí cómo querés que te vean tus rivales:
          </p>
        </div>
      </div>
      <Form>
        <input
          type="text"
          maxLength={24}
          value={value}
          placeholder="Tu apodo"
          autoComplete="nickname"
          autoCorrect="off"
          spellCheck={false}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && save()}
        />
        <button type="button" onClick={save} disabled={!ok || saving}>
          {saving ? 'Guardando…' : 'Ponerme el apodo'}
        </button>
      </Form>
      {bad && <p className="err">Elegí otro apodo, por favor.</p>}
      <button type="button" className="later" onClick={() => setDismissed(true)}>
        Ahora no
      </button>
    </Card>
  );
}

const Card = styled.div`
  background: ${({ theme }) => theme.colors.surfaceAlt};
  border: 1px solid ${({ theme }) => theme.colors.gold};
  border-radius: ${({ theme }) => theme.radius};
  padding: 16px;
  margin-bottom: 16px;

  .hd {
    display: flex;
    gap: 12px;
    align-items: flex-start;
  }
  .emoji {
    font-size: var(--text-2xl);
    line-height: 1;
    flex: 0 0 auto;
  }
  h3 {
    margin: 0 0 6px;
    font-size: var(--text-lg);
  }
  p {
    margin: 0;
    font-size: var(--text-sm);
    line-height: 1.5;
    color: ${({ theme }) => theme.colors.textMuted};
  }
  p b {
    color: ${({ theme }) => theme.colors.text};
  }
  .err {
    color: #ff6b6b;
    font-size: var(--text-sm);
    margin: 8px 0 0;
  }
  .later {
    display: block;
    margin: 10px auto 0;
    background: none;
    border: none;
    color: ${({ theme }) => theme.colors.textMuted};
    text-decoration: underline;
    font-size: var(--text-xs);
  }
`;

const Form = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;

  input {
    flex: 1;
    min-width: 0;
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text};
    border: 1px solid ${({ theme }) => theme.colors.borderStrong};
    border-radius: 10px;
    padding: 10px 12px;
    font-size: var(--text-md);
  }
  button {
    flex: 0 0 auto;
    background: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.accentText};
    border: none;
    border-radius: 10px;
    padding: 0 16px;
    font-weight: 700;
  }
  button:disabled {
    opacity: 0.5;
  }
`;
